import qs from "query-string";
import React, { useState, useCallback } from "react";
import {
  Page,
  Button,
  LegacyCard,
  Pagination,
  LegacyStack,
  IndexTable,
  InlineStack,
  Text,
  Tooltip,
} from "@shopify/polaris";
import { SearchFieldAndMoreFilters } from "./support/SearchFieldAndMoreFilters";
import ImportProgressBanner from "../home/support/ImportProgressBanner";
import { downloadFile, useInventoryLevelHistories } from "../../api_utils/requests";
import { humanDateFormat } from "../../helper_functions/utils";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import { Footer } from "../common/Footer";
import { HorseLocationSelect } from "../common/Filters/HorseLocationSelect";
import { TruncatedText } from "../common/HorseVariant/TruncatedText";
import type { InventoryLevelHistory, QueryParams } from "../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { AdjustedByLink } from "./support/AdjustedByLink";
import HorseLink from "../common/HorseLink";

const columnNames = [
  { title: "Date" },
  { title: "Location" },
  { title: "Variant" },
  { title: "Inventory type", tooltipContent: "eg. Available, incoming, committed, etc." },
  { title: "Adjusted by", tooltipContent: "What caused the inventory quantity to change" },
  { title: "Adjustment", tooltipContent: "How much it was adjusted by" },
  { title: "Quantity", tooltipContent: "The quantity after it was adjusted" },
  { title: "Reason" },
  { title: "Comment" },
] satisfies NonEmptyArray<IndexTableHeading>;

const reasonMapping: Record<string, string> = {
  correction: "Correction",
  count_inventory: "Count inventory",
  received: "Received",
  return_restock: "Return restock",
  damaged: "Damaged",
  theft_or_loss: "Theft or loss",
  promotion_or_donation: "Promotion or donation",
  other: "Other",
};

const convertSnakeCaseToHumanReadableCapitalized = (str: string): string =>
  str.length > 0 ? str[0].toUpperCase() + str.split("_").join(" ").slice(1) : "";

// Verbs for the purchase order lifecycle events; titled with the PO's label
const purchaseOrderVerbs: Record<string, string> = {
  purchase_order_ordered: "ordered",
  purchase_order_ordered_undo: "order undone",
  purchase_order_updated: "updated",
  purchase_order_received: "received",
  purchase_order_receive_undo: "receive undone",
  purchase_order_closed: "closed",
  purchase_order_closed_undo: "reopened",
  purchase_order_deleted: "deleted",
};

// Verbs for the transfer order lifecycle events; titled with the TO's label
const transferOrderVerbs: Record<string, string> = {
  transfer_order_send: "sent",
  transfer_order_send_undo: "send undone",
  transfer_order_receive: "received",
  transfer_order_receive_undo: "receive undone",
  transfer_order_closed: "closed",
  transfer_order_deleted: "deleted",
};

const subtypeTitles: Record<string, string> = {
  reverse_fulfillment_order_line_item: "Return restocked",
  sync_from_shopify: "Sync from Shopify",
};

const missingReasonTitles: Record<string, string> = {
  track_sil: "Location added",
  tracked_to_untracked: "Untracked",
  untracked_to_tracked: "Tracked",
};

// Available released back from a fulfillment order. The fulfillment order's
// CURRENT status can title history only where it allows one version of it:
// cancelled and incomplete are terminal, so the row was that event. A closed
// (completed) fulfillment order releases nothing at completion — and an open
// one has had no terminal event — so those rows can only be a reduction
// (order edit). An unknown status claims nothing.
const fulfillmentOrderReleaseTitle = (status: string | null | undefined): string => {
  if (status === "cancelled") {
    return "Fulfillment order cancelled";
  }
  if (status === "incomplete") {
    return "Fulfillment order closed";
  }
  if (status) {
    return "Fulfillment order reduced";
  }
  return "Fulfillment order";
};

const adjustedBy = (inventoryLevelHistory: InventoryLevelHistory): React.ReactElement | string => {
  const { url, subtype } = inventoryLevelHistory;
  let adjustedByTitle: string;
  if (
    subtype === "fulfillment_order" &&
    inventoryLevelHistory.inventory_name === "available" &&
    inventoryLevelHistory.quantity_change > 0
  ) {
    adjustedByTitle = fulfillmentOrderReleaseTitle(inventoryLevelHistory.fulfillment_order_status);
  } else if (inventoryLevelHistory.missing_inventory_adjustment_reason === "made_empty") {
    adjustedByTitle = "Inventory deleted";
  } else if (subtype && purchaseOrderVerbs[subtype]) {
    const label = inventoryLevelHistory.inventory_adjustment?.adjustable?.purchase_order?.label;
    adjustedByTitle = `Purchase order ${label ? `${label} ` : ""}${purchaseOrderVerbs[subtype]}`;
  } else if (subtype && transferOrderVerbs[subtype]) {
    const label = inventoryLevelHistory.inventory_adjustment?.adjustable?.transfer_order?.label;
    adjustedByTitle = `Transfer order ${label ? `${label} ` : ""}${transferOrderVerbs[subtype]}`;
  } else if (subtype && subtypeTitles[subtype]) {
    adjustedByTitle = subtypeTitles[subtype];
  } else if (missingReasonTitles[inventoryLevelHistory.missing_inventory_adjustment_reason]) {
    adjustedByTitle = missingReasonTitles[inventoryLevelHistory.missing_inventory_adjustment_reason];
  } else {
    adjustedByTitle = convertSnakeCaseToHumanReadableCapitalized(
      subtype || inventoryLevelHistory.missing_inventory_adjustment_reason || "",
    );
  }
  let adjustedByElement = <AdjustedByLink title={adjustedByTitle} url={url} />;

  if (inventoryLevelHistory.subtype === "bundle_child") {
    const source_inventory_adjustment = inventoryLevelHistory.inventory_adjustment.adjustable.subtype;
    const sourceAdjustedByTitle = convertSnakeCaseToHumanReadableCapitalized(source_inventory_adjustment);
    if (inventoryLevelHistory.inventory_adjustment.adjustable.url) {
      adjustedByElement = (
        <>
          {adjustedByElement}
          &nbsp;
          <AdjustedByLink
            title={`(${sourceAdjustedByTitle})`}
            url={inventoryLevelHistory.inventory_adjustment.adjustable.url}
          />
        </>
      );
    } else {
      adjustedByElement = (
        <>
          {adjustedByElement}
          &nbsp;
          {`(${sourceAdjustedByTitle})`}
        </>
      );
    }
  }

  if (inventoryLevelHistory.missing_inventory_adjustment_reason === "made_empty") {
    return (
      <Tooltip content="Either the location or variant was deleted">
        <Text as="span">{adjustedByElement}</Text>
      </Tooltip>
    );
  } else {
    return adjustedByElement;
  }
};

const inventoryLevelHistoryHelpPageUrl =
  "https://horse-inventory.notion.site/Inventory-History-f54ca590f6774698bd19cb5a5977d79c";

function InventoryLevelHistories(): React.ReactElement {
  const parsedUrlSearch = qs.parse(window.location.search);
  const [pageNum, setPageNum] = useState(Number(parsedUrlSearch.page) || 1);
  const [filters, setFilters] = useState<QueryParams>(parsedUrlSearch);
  const initHorseLocationId = parsedUrlSearch.horse_location_id as string;
  const [horseLocationId, setHorseLocationId] = useState<string>(initHorseLocationId);

  const formatReason = (reason: string): string => {
    return reasonMapping[reason] || reason;
  };
  const query = {
    ...filters,
    horse_location_id: horseLocationId,
    page: pageNum,
  };
  const { data, isFetching: isLoading } = useInventoryLevelHistories(query);
  const { rows: inventoryLevelHistories, hasNext, hasPrev } = data;

  const setFiltersAndResetPage = useCallback((newFilters: QueryParams) => {
    setFilters(newFilters);
    setPageNum(1);
  }, []);

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementsByTagName("body")[0]?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  return (
    <Page
      fullWidth
      primaryAction={
        <Button
          onClick={async (): Promise<void> => {
            const filename = `Horse Inventory History.csv`;
            await downloadFile("/inventory_level_histories.csv", filename, filters);
          }}
          removeUnderline
          size="medium"
          variant="monochromePlain"
        >
          Export CSV
        </Button>
      }
      title="Inventory history"
    >
      <ImportProgressBanner />
      <LegacyCard>
        <LegacyCard.Section>
          <InlineStack blockAlign="start" gap="300">
            <HorseLocationSelect horseLocationId={horseLocationId} setHorseLocationId={setHorseLocationId} />
          </InlineStack>
        </LegacyCard.Section>
        <SearchFieldAndMoreFilters setFilters={setFiltersAndResetPage} />
        <IndexTable
          headings={columnNames}
          itemCount={inventoryLevelHistories.length}
          loading={isLoading}
          selectable={false}
        >
          {inventoryLevelHistories.map((inventoryLevelHistory, index) => (
            <IndexTable.Row id={inventoryLevelHistory.id.toString()} key={inventoryLevelHistory.id} position={index}>
              <IndexTable.Cell>{humanDateFormat(new Date(inventoryLevelHistory.created_at))}</IndexTable.Cell>
              <IndexTable.Cell>
                <TruncatedText>
                  <HorseLink url={`/horse_locations/${inventoryLevelHistory.horse_location_id}`}>
                    <div style={{ maxWidth: "160px" }}>
                      <Text as="p" truncate>
                        {inventoryLevelHistory.horse_location_name}
                      </Text>
                    </div>
                  </HorseLink>
                </TruncatedText>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <div className="medium_hv">
                  <HorseVariant
                    key={inventoryLevelHistory.horse_variant_id}
                    variant={{
                      id: inventoryLevelHistory.horse_variant_id,
                      variant_title: inventoryLevelHistory.horse_variant_variant_title,
                      product_title: inventoryLevelHistory.horse_variant_title,
                      sku: inventoryLevelHistory.horse_variant_sku,
                      vendor: inventoryLevelHistory.horse_variant_vendor,
                      product_type: inventoryLevelHistory.horse_variant_product_type,
                      image_src: inventoryLevelHistory.horse_variant_image_src,
                      discarded: inventoryLevelHistory.horse_variant_discarded,
                    }}
                  />
                </div>
              </IndexTable.Cell>
              <IndexTable.Cell>
                {convertSnakeCaseToHumanReadableCapitalized(inventoryLevelHistory.inventory_name)}
              </IndexTable.Cell>
              <IndexTable.Cell>{adjustedBy(inventoryLevelHistory)}</IndexTable.Cell>
              <IndexTable.Cell>{inventoryLevelHistory.quantity_change}</IndexTable.Cell>
              <IndexTable.Cell>
                {Number.isInteger(inventoryLevelHistory.new_quantity) ? inventoryLevelHistory.new_quantity : "∞"}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {inventoryLevelHistory.reason ? formatReason(inventoryLevelHistory.reason) : "-"}
              </IndexTable.Cell>
              <IndexTable.Cell>{inventoryLevelHistory.comment ? inventoryLevelHistory.comment : "-"}</IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
        <LegacyCard.Section>
          <LegacyStack alignment="center" distribution="center">
            <Pagination
              hasNext={hasNext}
              hasPrevious={hasPrev}
              nextKeys={[39]}
              onNext={nextPage}
              onPrevious={prevPage}
              previousKeys={[37]}
            />
          </LegacyStack>
        </LegacyCard.Section>
      </LegacyCard>
      <Footer pageTitle="inventory history" url={inventoryLevelHistoryHelpPageUrl} />
    </Page>
  );
}

export default InventoryLevelHistories;
